import { plain } from "@/components/site/RichText";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { ContentPage, waLink } from "@/components/site/ContentPage";
import { pageHead, SITE_URL } from "@/lib/seo";
import portrait3 from "@/assets/kk-portrait-3.webp";

const TITLE = "Primeira consulta de cirurgia plástica | Dr. Kelson";
const DESCRIPTION =
  "Veja como funciona a primeira consulta com o Dr. Kelson Kawamura: escuta, avaliação física, registro, possibilidades e orientações completas.";
const PATH = "/primeira-consulta-cirurgia-plastica";

export const Route = createFileRoute("/primeira-consulta-cirurgia-plastica")({
  head: () => pageHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: PrimeiraConsultaPage,
});

const FAQ = [
  {
    q: "Preciso levar exames na primeira consulta?",
    a: "Se já possui exames relacionados à queixa ou ao histórico de saúde, pode levá-los. Novos exames são solicitados apenas quando necessários.",
  },
  {
    q: "Posso levar fotos de referência?",
    a: "Sim. Elas podem ajudar a explicar preferências, mas não significam que o mesmo resultado seja reproduzível em outra anatomia.",
  },
  {
    q: "A cirurgia é marcada no mesmo dia?",
    a: "A consulta inicia o planejamento. A marcação depende da indicação, exames, disponibilidade e decisão consciente do paciente.",
  },
  {
    q: "O valor é informado na consulta?",
    a: "O orçamento pode ser definido após a avaliação e o desenho do **plano cirúrgico**, porque varia conforme procedimento e estrutura.",
  },
  {
    q: "Posso ir acompanhada?",
    a: "A presença de um acompanhante pode ajudar na compreensão e no planejamento, conforme a preferência do paciente e a rotina do consultório.",
  },
];

function PrimeiraConsultaPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    url: `${SITE_URL}${PATH}`,
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: plain(f.a) },
    })),
  };

  return (
    <SiteShell>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <ContentPage
        hideSideNav
        eyebrow="Jornada do paciente"
        h1="Como funciona a primeira consulta"
        heroTitle="Uma consulta para avaliar, esclarecer e planejar"
        lead="A primeira consulta é um momento de escuta, avaliação cuidadosa e conversa transparente. O objetivo é compreender a queixa, analisar possibilidades e construir um plano seguro e realista."
        image={portrait3}
        imageAlt="Dr. Kelson Kawamura durante atendimento em consultório"
        crumbs={[{ label: "Primeira consulta" }]}
        ctaLabel="Agendar"
        ctaHref={waLink("Olá! Gostaria de agendar a primeira consulta com o Dr. Kelson Kawamura.")}
        ctaSecondary={{ label: "Procedimentos", href: "/especialidades" }}
        blocks={[
          {
            h: "1. Escuta inicial e anamnese",
            p: [
              "A conversa começa pelo histórico de saúde, cirurgias anteriores, medicamentos, alergias, hábitos, gestações, variações de peso e objetivos. Também é o momento de explicar o que incomoda e quais mudanças são desejadas.",
            ],
          },
          {
            h: "2. Avaliação clínica e física",
            p: [
              "O exame físico permite analisar anatomia, proporções, qualidade da pele, cicatrizes e fatores que interferem na indicação e na **segurança**. Quando necessário, o médico pode solicitar exames ou avaliações complementares.",
            ],
          },
          {
            h: "3. Registro fotográfico",
            p: [
              "Fotografias clínicas podem ser realizadas para documentação, comparação técnica e planejamento cirúrgico individualizado. O uso das imagens segue a autorização e as finalidades explicadas ao paciente.",
            ],
          },
          {
            h: "4. Possibilidades e limites",
            p: [
              "O Dr. Kelson apresenta as opções possíveis, explica o que cada técnica pode ou não tratar e conversa sobre cicatrizes, riscos, limitações e expectativas. Nem toda queixa exige cirurgia, e nem toda combinação é recomendada.",
            ],
          },
          {
            h: "5. Orientações completas",
            p: [
              "Quando existe indicação, o paciente recebe orientações sobre exames, hospital, anestesia, recuperação, afastamento, suporte em casa e retornos. O orçamento é elaborado conforme o plano individual.",
            ],
          },
          {
            h: "Como se preparar para a consulta",
            list: [
              "Leve informações sobre doenças, medicamentos e cirurgias anteriores.",
              "Anote dúvidas sobre recuperação, cicatrizes, internação e retorno ao trabalho.",
              "Compartilhe planos de gestação, perda de peso ou mudanças importantes na rotina.",
              "Evite escolher uma cirurgia apenas por tendência ou por comparação com outra pessoa.",
            ],
          },
        ]}
        related={[
          { label: "Cirurgias plásticas em Foz do Iguaçu", to: "/especialidades" },
          { label: "Dr. Kelson Kawamura", to: "/dr-kelson-kawamura" },
          { label: "Consultório em Foz do Iguaçu", to: "/consultorio-cirurgia-plastica-foz-do-iguacu" },
        ]}
        faq={FAQ}
      />
    </SiteShell>
  );
}
